import React, { useEffect, useState } from "react"

import { Row, Col } from "reactstrap"

// Pages Components
import Miniwidget from "./Miniwidget"
import ProfileViews from "./profileViews"
import Reach from "./reach"
import Impressions from "./impressions"
import Notifications from "./notifications"
import Inbox from "./inbox"
import RecentActivity from "./recent-activity"
import WidgetUser from "./widget-user"
import { useSelector, useDispatch, connect } from "react-redux"
import YearlySales from "./yearly-sales"
import LatestTransactions from "./latest-transactions"
import LatestOrders from "./latest-orders"
import PostsList from "./posts_list"

//Import Action to copy breadcrumb items from local state to redux state
import { setBreadcrumbItems } from "../../store/actions"
import SnackbarComponent from "components/SnackBar"
import axios from "axios"
import PostDetails from "./PostDetails"
import Chat from "./chat"
import CreatePost from "./CreatePost"

const FacebookDashboard = props => {
  const { organizations, isConsolidatedView } = useSelector(
    state => state.organizationReducer,
  )
  const [pageDetails, setPageDetails] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)
  const [reviews, setReviews] = useState([])
  const [postClicked, setPostClicked] = useState(false)
  const [viewMode, setViewMode] = useState("Month")
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [selectedPost, setSelectedPost] = useState(null)
  const [posts, setPosts] = useState([])
  const [reports, setReports] = useState([])
  const [success, setSuccess] = useState(null)
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )

  document.title = "Dashboard | Reputation Management"

  const breadcrumbItems = [
    {
      title: `${activeOrganization.name} (${activeOrganization.type})`,
      link: "/dashboard",
    },
    { title: "Instagram", link: "#" },
  ]

  const fetchInstagramInsights = async () => {
    try {
      setLoading(true)

      // Get list of Instagram pages depending on view mode
      const instaPages = isConsolidatedView
        ? organizations.flatMap(org => org.Organization.InstagramIntegrations)
        : activeOrganization.InstagramIntegrations

      // SUM VARIABLES
      let totalFollowers = 0
      let totalFollows = 0
      let totalMediaCount = 0
      let totalReachCurrent = 0
      let totalReachPrevious = 0

      // Utility
      const calcDiff = (current, previous) => {
        if (previous === 0) return "+0%"
        const diff = ((current - previous) / previous) * 100
        return `${diff >= 0 ? "+" : ""}${diff.toFixed(2)}%`
      }

      // Determine period based on viewMode
      const period = viewMode === "Month" ? "days_28" : "week"

      // ---------------------------------------
      // LOOP THROUGH EACH INSTAGRAM PAGE
      // ---------------------------------------
      for (const page of instaPages) {
        const { pageId, pageToken } = page

        // Fetch total followers, follows, and media count
        const totalFollowersResponse = await axios.get(
          `https://graph.facebook.com/v20.0/${pageId}`,
          {
            params: {
              access_token: pageToken,
              fields: "followers_count,follows_count,media_count",
            },
          },
        )

        totalFollowers += totalFollowersResponse.data.followers_count || 0
        totalFollows += totalFollowersResponse.data.follows_count || 0
        totalMediaCount += totalFollowersResponse.data.media_count || 0

        // =======================
        //   MONTH / WEEK MODES
        // =======================
        if (viewMode === "Month" || viewMode === "Week") {
          // Fetch reach data for the selected period
          const reachResponse = await axios.get(
            `https://graph.facebook.com/v20.0/${pageId}/insights`,
            {
              params: {
                access_token: pageToken,
                metric: "reach",
                period,
              },
            },
          )

          const reachValues = reachResponse.data.data[0]?.values || []
          totalReachCurrent += reachValues[1]?.value || 0
          totalReachPrevious += reachValues[0]?.value || 0
        }

        // =======================
        //     CUSTOM RANGE
        // =======================
        else {
          let currentStart = new Date(fromDate)
          let currentEnd = new Date(toDate)
          const rangeDays = (currentEnd - currentStart) / (1000 * 60 * 60 * 24)

          const MAX_DAYS = 180
          if (rangeDays > MAX_DAYS) {
            setErr("Date range exceeded, max allowed is 6 months.")
            return
          }

          const GROUP = rangeDays > 28 ? 28 : rangeDays > 7 ? 7 : rangeDays

          for (let i = 0; i < rangeDays; i += GROUP) {
            const gStart = new Date(currentStart)
            gStart.setDate(gStart.getDate() + i)

            const gEnd = new Date(gStart)
            gEnd.setDate(gEnd.getDate() + GROUP - 1)
            if (gEnd > currentEnd) gEnd = currentEnd

            const pStart = new Date(gStart)
            pStart.setDate(pStart.getDate() - rangeDays)

            const pEnd = new Date(gEnd)
            pEnd.setDate(pEnd.getDate() - rangeDays)

            const currResp = await axios.get(
              `https://graph.facebook.com/v20.0/${pageId}/insights`,
              {
                params: {
                  access_token: pageToken,
                  metric: "reach",
                  period: "day",
                  since: gStart / 1000,
                  until: gEnd / 1000,
                },
              },
            )

            const prevResp = await axios.get(
              `https://graph.facebook.com/v20.0/${pageId}/insights`,
              {
                params: {
                  access_token: pageToken,
                  metric: "reach",
                  period: "day",
                  since: pStart / 1000,
                  until: pEnd / 1000,
                },
              },
            )

            const sum = vals =>
              vals.reduce((acc, item) => acc + (item.value || 0), 0)

            totalReachCurrent += sum(currResp.data.data[0]?.values || [])
            totalReachPrevious += sum(prevResp.data.data[0]?.values || [])
          }
        }
      }

      // ---------------------------------------
      // SET FINAL REPORTS
      // ---------------------------------------
      setReports([
        {
          title: "Followers",
          iconClass: "account-group",
          total: totalFollowers,
          average: null,
          badgecolor: "info",
        },
        {
          title: "Follows",
          iconClass: "thumb-up",
          total: totalFollows,
          average: null,
          badgecolor: "info",
        },
        {
          title: "Posts Count",
          iconClass: "message-draw",
          total: totalMediaCount,
          average: null,
          badgecolor: "info",
        },
        {
          title: "Reach",
          iconClass: "star",
          total: totalReachCurrent,
          average: calcDiff(totalReachCurrent, totalReachPrevious),
          badgecolor:
            totalReachCurrent >= totalReachPrevious ? "info" : "danger",
        },
      ])

      setLoading(false)
    } catch (error) {
      console.error("Error fetching Instagram insights:", error)
      setErr("Error fetching Instagram insights.")
      setLoading(false)
    }
  }

  const fetchPosts = async () => {
    try {
      setLoading(true)

      // Determine which Instagram pages to fetch from
      const instaPages = isConsolidatedView
        ? organizations.flatMap(org =>
            org.Organization.InstagramIntegrations.map(page => ({
              ...page,
              organizationName: org.Organization.name,
            })),
          )
        : activeOrganization.InstagramIntegrations.map(page => ({
            ...page,
            organizationName: activeOrganization.name,
          }))

      let allPosts = []

      for (const page of instaPages) {
        const { pageId, pageToken, organizationName } = page

        const response = await axios.get(
          `https://graph.facebook.com/v20.0/${pageId}/media`,
          {
            params: {
              access_token: pageToken,
              fields:
                "id,caption,media_type,media_url,permalink,thumbnail_url,timestamp",
            },
          },
        )

        const postsWithOrg = (response.data.data || []).map(post => ({
          ...post,
          organizationName,
          pageId,
          pageToken,
        }))

        console.log("Fetched posts for page:", pageId, postsWithOrg)
        allPosts = [...allPosts, ...postsWithOrg]
      }

      setPosts(allPosts)
      setLoading(false)
    } catch (err) {
      console.error("Fetch posts error:", err)
      setErr("Error fetching posts.")
      setLoading(false)
    }
  }

  useEffect(() => {
    props.setBreadcrumbItems(`Instagram`, breadcrumbItems)
    fetchPosts()
  }, [activeOrganization, isConsolidatedView, organizations])

  useEffect(() => {
    fetchInstagramInsights()
  }, [viewMode, activeOrganization, isConsolidatedView, organizations])

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />
      {postClicked ? (
        <PostDetails
          post={selectedPost}
          setErr={setErr}
          setSuccess={setSuccess}
          setPostClicked={setPostClicked}
          setSelectedPost={setSelectedPost}
        />
      ) : (
        <>
          <Miniwidget
            reports={reports}
            setViewMode={setViewMode}
            viewMode={viewMode}
            loading={loading}
            callBack={fetchInstagramInsights}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
          />

          <Row>
            <Col xl="12">
              <Reach reviews={reviews} loading={loading} />
            </Col>
          </Row>

          <Row>
            <Col xl="12">
              <ProfileViews />
            </Col>
          </Row>

          <Row>
            <Col xl={12}>
              <PostsList
                loading={loading}
                posts={posts}
                isConsolidatedView={isConsolidatedView}
                setPostClicked={setPostClicked}
                setSelectedPost={setSelectedPost}
              />
            </Col>
          </Row>
        </>
      )}
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(FacebookDashboard)